<template>
    <InputRangeTwoKnobbed
        v-model="m"
        v-qa="'range-segmented'"
        :data-qa-step="step"
        :data-qa-min="min"
        :data-qa-max="max"
        :data-qa-value="JSON.stringify(modelValue)"
        :data-qa-segments-config="JSON.stringify(segmentsConfig)"
        :min="0"
        :max="NATIVE_RANGE"
        :step="1"
        :value-formatter="getValueLabel"
        :min-label
        :max-label
        :debounce
    />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { type InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import InputRangeTwoKnobbed from '@/components/ui/inputs/range/InputRangeTwoKnobbed.vue'
import {
    convertRealToNative,
    NATIVE_RANGE,
    segmentsConfigToSegments,
    type Segment,
    type SegmentConfigFinal,
    type SegmentedProps,
} from './input-range-segmented'

const {
    segmentsConfig,
    min = 0,
    max = 100,
    step,
    modelValue,
} = defineProps<SegmentedProps>()

// Segments check
// TODO add more checks for inter-segment borders

const lastSegment = segmentsConfig[segmentsConfig.length - 1]
if (lastSegment && lastSegment[2] !== 1) {
    throw new Error('Last segment should end on 1')
}

const segments = computed<SegmentConfigFinal>(() =>
    segmentsConfigToSegments(segmentsConfig)
)

const getSegmentForNativeValue = (value: number): Segment => {
    const segment = segments.value.find(
        (s, index, arr) =>
            value >= s[2] &&
            (index === arr.length - 1 ? value <= s[3] : value < s[3])
    )

    if (segment) {
        return segment
    }

    if (value > NATIVE_RANGE) {
        const segment = segments.value[segments.value.length - 1]

        if (segment) {
            return segment
        }

        return segments.value[1]
    }

    return segments.value[0]
}

const calculateNativeValueRatioInSegment = (value: number, segment: Segment) =>
    (value - segment[2]) / (segment[3] - segment[2])

const calculateRealValueInSegment = (ratio: number, segment: Segment) =>
    segment[0] + (segment[1] - segment[0]) * ratio

const convertNativeToReal = (value: number) => {
    if (value === 0) {
        return min
    }

    if (value === NATIVE_RANGE) {
        return max
    }

    value = value / NATIVE_RANGE

    const segment = getSegmentForNativeValue(value)
    const ratio = calculateNativeValueRatioInSegment(value, segment)
    const realValue = calculateRealValueInSegment(ratio, segment)
    const stepRatio = 1 / step
    const stepped = (Math.ceil(realValue * stepRatio) / stepRatio).toFixed(2)

    return Number(stepped)
}

const emit = defineEmits<{
    'update:modelValue': [InputRangeModel]
}>()

const getValueLabel = (inputValue: number) => {
    return convertNativeToReal(inputValue).toFixed(2)
}

const m = computed({
    get() {
        const valueMin = convertRealToNative(
            Math.max(modelValue?.[0] || min, min),
            segments.value,
            max
        )

        const valueMax = convertRealToNative(
            Math.min(modelValue?.[1] || max, max),
            segments.value,
            max
        )

        return [valueMin, valueMax] as [number, number]
    },
    set(rangeValue: InputRangeModel) {
        const result: [number, number] = [
            convertNativeToReal(rangeValue[0] || 0),
            convertNativeToReal(rangeValue[1] || NATIVE_RANGE),
        ]

        emit('update:modelValue', result)
    },
})
</script>
